import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { UserPlus, Copy } from "lucide-react";

export default function SettingsPage() {
  const { api, user, isAdmin, isClientAdmin } = useAuth();
  const [profile, setProfile] = useState({ full_name: "", job_title: "", linkedin_url: "" });
  const [org, setOrg] = useState(null);
  const [users, setUsers] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [passwords, setPasswords] = useState({ current: "", new_password: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/profile").then(r => {
      const d = r.data;
      setProfile({ full_name: d.full_name || "", job_title: d.job_title || "", linkedin_url: d.linkedin_url || "" });
    }).catch(() => {});
    api.get("/settings/organization").then(r => setOrg(r.data)).catch(() => {});
    if (isClientAdmin || isAdmin) {
      api.get("/settings/users").then(r => setUsers(r.data)).catch(() => {});
    }
  }, [api, isAdmin, isClientAdmin]);

  const handleProfileSave = async () => {
    setSaving(true);
    try { await api.put("/settings/profile", profile); toast.success("Profile updated"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const handlePasswordChange = async () => {
    try {
      await api.put("/settings/password", { current_password: passwords.current, new_password: passwords.new_password });
      toast.success("Password changed");
      setPasswords({ current: "", new_password: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleInvite = async () => {
    if (!inviteEmail) return;
    try {
      const res = await api.post("/settings/users/invite", { email: inviteEmail });
      if (res.data.status === "pending_approval") {
        toast.success("Invite submitted for admin approval");
      } else {
        toast.success("Invite sent");
      }
      setInviteEmail("");
      api.get("/settings/users").then(r => setUsers(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || "Invite failed"); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="settings-page">
      <h1 className="text-2xl font-heading text-slate-900 mb-6">Settings</h1>
      <Tabs defaultValue="profile">
        <TabsList className="mb-6">
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          {(isClientAdmin || isAdmin) && <TabsTrigger value="organization" data-testid="tab-org">Organization</TabsTrigger>}
          {(isClientAdmin || isAdmin) && <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>}
        </TabsList>

        <TabsContent value="profile">
          <Card className="animate-fadeIn"><CardContent className="p-6 space-y-4">
            <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Full Name</Label><Input data-testid="profile-name" value={profile.full_name} onChange={e => setProfile(p => ({ ...p, full_name: e.target.value }))} /></div>
            <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Job Title</Label><Input data-testid="profile-title" value={profile.job_title} onChange={e => setProfile(p => ({ ...p, job_title: e.target.value }))} /></div>
            <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">LinkedIn URL</Label><Input data-testid="profile-linkedin" value={profile.linkedin_url} onChange={e => setProfile(p => ({ ...p, linkedin_url: e.target.value }))} /></div>
            <Button data-testid="save-profile-btn" onClick={handleProfileSave} disabled={saving} className="bg-evergreen-950 hover:bg-evergreen-900 text-white">{saving ? "Saving..." : "Save Profile"}</Button>
            <Separator className="my-6" />
            <h3 className="font-heading text-slate-900">Change Password</h3>
            <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Current Password</Label><Input type="password" value={passwords.current} onChange={e => setPasswords(p => ({ ...p, current: e.target.value }))} /></div>
            <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">New Password</Label><Input type="password" value={passwords.new_password} onChange={e => setPasswords(p => ({ ...p, new_password: e.target.value }))} /></div>
            <Button data-testid="change-password-btn" onClick={handlePasswordChange} variant="outline">Change Password</Button>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="organization">
          {org && (
            <Card className="animate-fadeIn"><CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-heading text-slate-900">{org.name}</h3>
                <Badge className="bg-lime-100 text-lime-700 border-0">{org.contract_tier}</Badge>
                <Badge className={org.status === "active" ? "bg-lime-100 text-lime-700 border-0" : "bg-red-100 text-red-700 border-0"}>{org.status}</Badge>
              </div>
              <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Suppression Companies</Label>
                <div className="flex flex-wrap gap-1.5">{(org.suppression_names || []).map(n => <Badge key={n} variant="outline" className="text-xs">{n}</Badge>)}</div></div>
              <div><Label className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-2 block">Suppression Domains</Label>
                <div className="flex flex-wrap gap-1.5">{(org.suppression_domains || []).map(d => <Badge key={d} variant="outline" className="text-xs font-mono">{d}</Badge>)}</div></div>
            </CardContent></Card>
          )}
        </TabsContent>

        <TabsContent value="users">
          <Card className="animate-fadeIn"><CardContent className="p-6">
            <div className="flex gap-3 mb-6">
              <Input data-testid="invite-email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="Email to invite..." className="flex-1" />
              <Button data-testid="send-invite-btn" onClick={handleInvite} className="bg-lime-600 hover:bg-lime-700 text-white"><UserPlus className="w-4 h-4 mr-1" /> Invite</Button>
            </div>
            <div className="space-y-2">
              {users.map(u => (
                <div key={u.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{u.full_name}</p>
                    <p className="text-xs text-slate-500">{u.email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={u.role === "client_admin" ? "bg-blue-100 text-blue-700 border-0" : "bg-slate-100 text-slate-600 border-0"} variant="secondary">{u.role}</Badge>
                    <Badge className={u.status === "active" ? "bg-lime-100 text-lime-700 border-0" : "bg-red-100 text-red-700 border-0"}>{u.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
